<template>
  <div class="kent-staff-doc">
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">
          {{ reportBrandLabel }}
          <span class="text-primary">| Appointment Letter</span>
        </h4>
        <b-badge v-show="!loading" variant="light-primary" class="ml-2">
          {{ reportItems.length }} Letters
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3.5rem; height: 3.5rem;" />
      <p class="mt-3 font-weight-bold">Loading appointment letters...</p>
    </div>

    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button variant="primary" class="mt-3" @click="loadReportData">
        Retry
      </b-button>
    </div>

    <div v-else class="documents-wrap">
      <div
        v-for="(staff, index) in reportItems"
        :key="staff.id || index"
        class="letter-set"
      >
        <div class="letter-page letter-page--single">
          <div class="letter-sheet">
            <header class="letterhead">
              <div class="letterhead__body">
                <img
                  :src="schoolLogo"
                  alt="Kent Schooling System"
                  class="letterhead__logo"
                />
                <h2 class="letterhead__school">{{ schoolName }}</h2>
                <p v-if="campusName" class="letterhead__campus">{{ campusName }}</p>
                <p v-if="campusAddress" class="letterhead__address">
                  {{ campusAddress }}
                </p>
              </div>
            </header>

            <div class="letter-date-block">
              <span class="letter-date-label">Dated:</span>
              <span class="letter-date-value">{{ currentDateLabel }}</span>
            </div>

            <h1 class="letter-title">
              <span class="letter-title__text">Letter of Intent</span>
            </h1>

            <section class="details-card">
              <div class="details-grid">
                <div class="detail-item">
                  <span class="detail-label">Name</span>
                  <span class="detail-value">{{ staffDisplayName(staff) }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Title</span>
                  <span class="detail-value">{{ staffDesignation(staff) }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Reporting to</span>
                  <span class="detail-value">{{
                    staffReportingTo(staff) || "—"
                  }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Commencement Date</span>
                  <span class="detail-value">{{
                    formatKentDate(staffAppointedOn(staff))
                  }}</span>
                </div>
                <div class="detail-item detail-item--full">
                  <span class="detail-label">Monthly Remuneration</span>
                  <span class="detail-value">
                    A Sum of PKR
                    <strong class="detail-salary">{{ remunerationLabel(staff) }}</strong>
                    /= per month as a gross salary
                  </span>
                </div>
                <div class="detail-item detail-item--full">
                  <span class="detail-label">Annual Review</span>
                  <span class="detail-value">
                    Remuneration will be reviewed annually in August.
                  </span>
                </div>
              </div>
            </section>

          <div class="letter-clauses">
            <p class="clauses-heading">
              <span class="clauses-heading__icon"></span>
              Terms &amp; Conditions
            </p>
            <ol class="clauses-list">
              <li>
                You will be governed by the rules and regulation of the school and
                also will be bound to follow the instructions given by the Head of
                the School in the interest of the institution.
              </li>
              <li>
                The first 3 months of your services are entirely a probation period.
                During this period, your work is evaluated and if it is found to be
                unsatisfactory, your services will be terminated without serving you
                any notice. After the completion of probation period you will be
                entitled for the confirmation of the service and after confirmation of
                the services are liable to be terminated on the negligence of duties,
                misconduct and unsatisfactory work, having given you an opportunity
                to explain your position.
              </li>
              <li>
                You will be awarded annual increment which will depend upon the
                quality of your work, punctuality, regularity and over the reports
                which will be submitted by the concerned coordinator.
              </li>
              <li>
                It is agreed between you and the management that you will serve this
                institution for ONE COMPLETE SESSION, during this time you CANNOT
                LEAVE your job before the Session ends. Failed to abide by this
                clause will result in complete DEDUCTION OF YOUR SECURITY DEPOSITS
                and the management will be at liberty to take any action against you
                including forfeiture of your Current Month's Salary.
              </li>
              <li>
                Your services will be transferable to any institution/campus managed
                by the competent authority.
              </li>
              <li>
                A continuous absence of FIVE DAYS without any written application
                will be considered that you are no longer interested in your job and
                therefore a substitute will be arranged immediately to avoid further
                loss of the students' education which will automatically cause
                termination of your services without any notice. Either party will be
                required to assign any reason for the termination of the service.
              </li>
              <li>
                In case of New Employee, you will not be paid earned leaves summer
                or winter vacations before the completion of ONE YEAR of the services.
              </li>
            </ol>
          </div>

          <div class="acceptance-box">
            <p class="acceptance-text">
              I have gone through the terms and conditions mentioned above. I accept
              the same and hereby agree to abide by them during the tenure of my
              services.
            </p>
          </div>

          <div class="signature-section">
            <p class="signature-intro">For {{ schoolName }},</p>
            <div class="signature-row">
              <div class="signature-block signature-block--employer">
                <div class="signature-space"></div>
                <div class="signature-line"></div>
                <p class="signature-name">Mrs. Samreen Farhan</p>
                <p class="signature-role">Chairperson.</p>
                <p class="signature-school">{{ schoolName }}</p>
              </div>
              <div class="signature-block signature-block--employee">
                <div class="signature-space"></div>
                <div class="signature-line"></div>
                <p class="signature-employee-label">Signature of Employee</p>
              </div>
            </div>
          </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from "bootstrap-vue";
import axios from "axios";
import {
  parseStaffIds,
  parseKentDepIds,
  loadKentCampusInfo,
  loadKentStaffRecords,
  loadKentStaffByDepartments,
  staffDisplayName,
  staffDesignation,
  formatKentDate,
  staffSalary,
  formatKentCurrency,
  staffReportingTo,
  isStaffLetterPrintDomain,
  defaultSchoolNameForDomain,
} from "./kentStaffPrintUtils";

export default {
  name: "KentAppointmentLetterPrint",
  components: {
    BButton,
    BSpinner,
    BBadge,
  },
  data() {
    return {
      loading: false,
      error: null,
      reportItems: [],
      departments: [],
      schoolName: defaultSchoolNameForDomain(
        (this.$store.state.report || "").replace(/_/g, "")
      ),
      campusName: "",
      campusAddress: "",
      schoolLogo: "/kennt_logo.png",
      db: "",
      cID: 0,
      staffIDs: [],
      depIDs: [],
      staffStatus: "present",
      reportDomain: (this.$store.state.report || "").replace(/_/g, ""),
    };
  },
  computed: {
    currentDateLabel() {
      return formatKentDate(new Date());
    },
    reportBrandLabel() {
      return this.schoolName || defaultSchoolNameForDomain(this.reportDomain);
    },
  },
  created() {
    this.initReport();
  },
  methods: {
    staffDisplayName,
    staffDesignation,
    formatKentDate,
    staffReportingTo,
    initReport() {
      if (!isStaffLetterPrintDomain(this.reportDomain)) {
        this.error = "This report is not available for this school.";
        return;
      }

      const query = this.$route.query;
      this.db = query.db || this.$store.state.userData.db;
      this.cID = Number(query.cID || this.$store.state.userData.cId || 0);
      this.staffIDs = parseStaffIds(query);
      this.depIDs = parseKentDepIds(query);
      this.staffStatus = query.status || "present";

      if (!this.db) {
        this.error = "Database identifier is missing.";
        return;
      }
      if (!this.staffIDs.length && !this.depIDs.length) {
        this.error =
          "No staff or department selected for appointment letters.";
        return;
      }

      this.loadReportData();
    },
    staffAppointedOn(staff) {
      return (
        staff?.appointedOn ||
        staff?.appointmentDate ||
        staff?.joiningDate ||
        null
      );
    },
    remunerationLabel(staff) {
      const salary = staffSalary(staff);
      if (salary === null) return "______________";
      return formatKentCurrency(salary);
    },
    async loadDepartments() {
      try {
        const response = await axios.get(
          `${this.$store.state.domain}StaffDepartments`,
          {
            params: { db: this.db, cId: this.cID },
            headers: {
              Authorization: `Bearer ${this.$store.state.userData?.token}`,
              accept: "application/json",
            },
          }
        );
        this.departments = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.data)
          ? response.data.data
          : [];
      } catch (e) {
        console.warn("Departments failed to load", e);
      }
    },
    async loadReportData() {
      this.loading = true;
      this.error = null;

      try {
        const campus = await loadKentCampusInfo(this, this.db, this.cID);
        this.schoolName = campus.schoolName;
        this.campusName = campus.campusName;
        this.campusAddress = campus.campusAddress;
        this.schoolLogo = campus.schoolLogo;

        await this.loadDepartments();
        if (this.staffIDs.length) {
          this.reportItems = await loadKentStaffRecords(
            this,
            this.db,
            this.cID,
            this.staffIDs
          );
        } else {
          this.reportItems = await loadKentStaffByDepartments(
            this,
            this.db,
            this.cID,
            this.depIDs,
            this.staffStatus
          );
        }

        if (!this.reportItems.length) {
          this.error = "No staff records found for the selected staff.";
        }
      } catch (err) {
        console.error("Kent appointment letter error:", err);
        this.error = "Failed to load staff records for appointment letters.";
      } finally {
        this.loading = false;
      }
    },
    print() {
      window.print();
    },
    close() {
      window.close();
    },
  },
};
</script>

<style lang="scss" scoped>
@import url("https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap");

$kent-navy: #1a2b56;
$font-main: "Inter", "Segoe UI", sans-serif;
$kent-navy-light: #2d4a7a;
$kent-gold: #c9a227;
$text-dark: #1f2937;
$text-muted: #6b7280;
$border-light: #e5e7eb;

.kent-staff-doc {
  background: #eef1f6;
  min-height: 100vh;
  padding: 2rem;
  font-family: "Inter", "Segoe UI", sans-serif;
  color: $text-dark;
}

.control-bar {
  background: #fff;
  padding: 1rem 1.5rem;
  margin-bottom: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 1rem;
  z-index: 10;
}

.action-buttons {
  display: flex;
  gap: 10px;
}

.btn-modern {
  display: inline-flex;
  align-items: center;
  padding: 10px 20px;
  border-radius: 10px;
  border: none;
  font-weight: 700;
  cursor: pointer;
}

.btn-print {
  background: linear-gradient(135deg, $kent-navy 0%, $kent-navy-light 100%);
  color: #fff;
}

.btn-close {
  background: #fff;
  border: 1px solid #dfe6e9;
  color: #636e72;
}

.documents-wrap {
  max-width: 210mm;
  margin: 0 auto;
}

.letter-set {
  margin-bottom: 2.5rem;
}

.letter-page {
  background: #fff;
  padding: 0;
  margin-bottom: 1.25rem;
  border-radius: 4px;
  box-shadow: 0 4px 24px rgba(26, 43, 86, 0.1);
  box-sizing: border-box;
  overflow: hidden;

  &--single {
    width: 210mm;
    height: 296mm;
    min-height: 296mm;
    max-height: 296mm;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    margin-left: auto;
    margin-right: auto;
  }
}

.letter-sheet {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 9mm 11mm 8mm;
  font-family: $font-main;
  font-size: 10.5pt;
  line-height: 1.48;
  color: $text-dark;
  box-sizing: border-box;
}

/* ─── Letterhead ─── */
.letterhead {
  text-align: center;
  margin: 0 0 0.5rem;
  flex-shrink: 0;

  &__accent {
    height: 4px;
    background: linear-gradient(
      90deg,
      $kent-navy 0%,
      $kent-navy-light 50%,
      $kent-navy 100%
    );
  }

  &__body {
    padding: 0.35rem 0 0.25rem;
    background: linear-gradient(
      180deg,
      rgba(26, 43, 86, 0.04) 0%,
      transparent 100%
    );
  }

  &__body--compact {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.65rem;
    padding: 0.65rem 1rem;
  }

  &__logo {
    width: 52px;
    height: auto;
    object-fit: contain;
    margin-bottom: 0.25rem;

    &--small {
      width: 36px;
      margin-bottom: 0;
    }
  }

  &__school {
    font-size: 1.15rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    margin: 0;
    color: $kent-navy;
    font-family: $font-main;
  }

  &__school-mini {
    font-size: 0.82rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: $kent-navy;
    font-family: $font-main;
  }

  &__campus {
    margin: 0.2rem 0 0;
    font-size: 0.88rem;
    font-weight: 600;
    color: $kent-navy-light;
    font-family: $font-main;
  }

  &__address {
    margin: 0.15rem 0 0;
    font-size: 0.78rem;
    color: $text-muted;
    font-family: $font-main;
  }

  &--compact {
    margin-bottom: 1.25rem;
  }
}

.letter-date-block {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.1rem;
  margin-bottom: 0.45rem;
  font-family: $font-main;
  flex-shrink: 0;
}

.letter-date-label {
  font-size: 0.95rem;
  font-weight: 600;
  color: $text-muted;
}

.letter-date-value {
  font-size: 1.05rem;
  font-weight: 700;
  color: $text-dark;
}

.letter-title {
  text-align: center;
  margin: 0 0 0.5rem;
  flex-shrink: 0;

  &__text {
    display: inline-block;
    font-size: 1.2rem;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #fff;
    background: $kent-navy;
    padding: 0.5rem 2rem;
    font-family: $font-main;
    border-radius: 2px;
    box-shadow: 0 2px 8px rgba(26, 43, 86, 0.2);
  }
}

/* ─── Details card ─── */
.details-card {
  border: 1px solid $border-light;
  border-radius: 4px;
  margin-bottom: 0.45rem;
  overflow: hidden;
  background: #fafbfc;
  flex-shrink: 0;
}

.details-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  padding: 0.5rem 0.85rem;
  border-bottom: 1px solid $border-light;
  border-right: 1px solid $border-light;

  &:nth-child(2n) {
    border-right: none;
  }

  &--full {
    grid-column: 1 / -1;
    border-right: none;
  }

  &:last-child,
  &:nth-last-child(2):nth-child(odd) {
    border-bottom: none;
  }
}

.detail-label {
  font-family: $font-main;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: $text-muted;
}

.detail-value {
  font-family: $font-main;
  font-size: 1rem;
  font-weight: 600;
  color: $text-dark;
  line-height: 1.5;
}

.detail-salary {
  color: $kent-navy;
  font-weight: 700;
}

/* ─── Clauses ─── */
.letter-clauses {
  margin-top: 0.15rem;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;

  &--continued {
    margin-top: 0;
  }
}

.clauses-heading {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: $font-main;
  font-size: 0.9rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: $kent-navy;
  margin-bottom: 0.35rem;
  flex-shrink: 0;

  &__icon {
    display: inline-block;
    width: 4px;
    height: 16px;
    background: $kent-gold;
    border-radius: 1px;
    flex-shrink: 0;
  }
}

.clauses-list {
  margin: 0;
  padding-left: 0;
  list-style: none;
  counter-reset: clause;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  li {
    position: relative;
    padding-left: 2rem;
    margin-bottom: 0.2rem;
    padding-bottom: 0;
    text-align: justify;
    font-family: $font-main;
    font-size: 9.5pt;
    line-height: 1.38;
    counter-increment: clause;

    &.clause-item--keep {
      page-break-inside: avoid;
      break-inside: avoid;
    }

    &:last-child {
      margin-bottom: 0;
    }

    &::before {
      content: counter(clause);
      position: absolute;
      left: 0;
      top: 0.15rem;
      width: 1.5rem;
      height: 1.5rem;
      background: $kent-navy;
      color: #fff;
      font-family: $font-main;
      font-size: 0.8rem;
      font-weight: 700;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      line-height: 1;
    }
  }

  &--resume {
    counter-reset: clause 4;
  }
}

/* ─── Acceptance & signatures ─── */
.acceptance-box {
  background: rgba(26, 43, 86, 0.05);
  border: 1px solid rgba(26, 43, 86, 0.12);
  border-left: 4px solid $kent-gold;
  border-radius: 4px;
  padding: 0.4rem 0.65rem;
  margin: 0.35rem 0 0.25rem;
  flex-shrink: 0;
  margin-top: auto;
}

.acceptance-text {
  margin: 0;
  text-align: justify;
  font-family: $font-main;
  font-size: 9.5pt;
  line-height: 1.42;
  font-style: italic;
  color: $text-dark;
}

.signature-section {
  margin-top: 0.25rem;
  flex-shrink: 0;
}

.signature-intro {
  margin-bottom: 0.65rem;
  font-family: $font-main;
  font-weight: 600;
  font-size: 1rem;
}

.signature-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2.5rem;
  margin-top: 0.35rem;
  align-items: start;
}

.signature-block {
  display: grid;
  grid-template-rows: auto auto 1fr;
  min-width: 0;
}

.signature-space {
  height: 2rem;
  min-height: 2rem;
}

.signature-line {
  border-top: 1px solid $text-dark;
  margin: 0;
}

.signature-name {
  margin: 0.3rem 0 0;
  font-weight: 700;
  font-size: 0.92rem;
  color: $text-dark;
  font-family: $font-main;
  line-height: 1.35;
}

.signature-role {
  margin: 0.1rem 0 0;
  font-size: 0.88rem;
  font-weight: 700;
  color: $text-dark;
  font-family: $font-main;
  line-height: 1.35;
}

.signature-school {
  margin: 0.15rem 0 0;
  font-weight: 700;
  text-align: left;
  font-size: 0.92rem;
  color: $text-dark;
  font-family: $font-main;
  line-height: 1.35;
}

.signature-employee-label {
  margin: 0.55rem 0 0;
  font-weight: 700;
  font-size: 0.95rem;
  color: $text-dark;
  font-family: $font-main;
  line-height: 1.35;
}

.signature-block--employee {
  .signature-employee-label {
    margin-top: 0.3rem;
  }
}

@media print {
  .no-print {
    display: none !important;
  }

  .kent-staff-doc {
    background: #fff;
    padding: 0;
    min-height: 0 !important;
    height: auto !important;
  }

  .documents-wrap {
    max-width: none;
    margin: 0;
    padding: 0;
  }

  .letter-set {
    margin: 0;
    padding: 0;
  }

  .letter-set + .letter-set {
    page-break-before: always;
    break-before: page;
  }

  .letter-page--single {
    width: 210mm !important;
    height: 296mm !important;
    min-height: 296mm !important;
    max-height: 296mm !important;
    overflow: hidden !important;
    box-shadow: none;
    border-radius: 0;
    margin: 0;
    padding: 0;
    page-break-inside: avoid !important;
    break-inside: avoid !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .letter-page--single .letter-sheet {
    display: flex !important;
    flex-direction: column !important;
    height: 100% !important;
    min-height: 0 !important;
    padding: 9mm 11mm 8mm !important;
    font-size: 10pt;
    font-weight: 600;
    line-height: 1.4;
    width: 100%;
    color: #000;
    overflow: hidden !important;
    box-sizing: border-box !important;
  }

  .letter-page--single .letterhead {
    margin: 0 0 0.35rem;

    &__body {
      padding: 0.2rem 0 0.15rem;
    }

    &__logo {
      width: 48px;
      margin-bottom: 0.15rem;
    }

    &__school {
      font-size: 12pt;
      font-weight: 700;
    }

    &__campus {
      font-size: 9.5pt;
      font-weight: 700;
      margin-top: 0.1rem;
    }

    &__address {
      font-size: 8.5pt;
      font-weight: 600;
    }
  }

  .letter-page--single .letter-date-block {
    margin-bottom: 0.35rem;
  }

  .letter-page--single .letter-date-label {
    font-size: 9pt;
    font-weight: 700;
  }

  .letter-page--single .letter-date-value {
    font-size: 10pt;
    font-weight: 700;
  }

  .letter-page--single .letter-title {
    margin-bottom: 0.35rem;

    &__text {
      font-size: 11pt;
      font-weight: 700;
      padding: 0.3rem 1.25rem;
    }
  }

  .letter-page--single .details-card {
    margin-bottom: 0.35rem;
    border: 2pt solid #000 !important;
    background: #fff !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .letter-page--single .detail-item {
    padding: 0.3rem 0.5rem;
    border-bottom: 1.5pt solid #000 !important;
    border-right: 1.5pt solid #000 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;

    &:last-child,
    &:nth-last-child(2):nth-child(odd) {
      border-bottom: 1.5pt solid #000 !important;
    }

    &:nth-child(2n) {
      border-right: none !important;
    }

    &--full {
      border-right: none !important;
    }
  }

  .letter-page--single .detail-label {
    font-size: 7.5pt;
    font-weight: 700;
    color: #000;
  }

  .letter-page--single .detail-value {
    font-size: 9pt;
    font-weight: 700;
    line-height: 1.35;
    color: #000;
  }

  .letter-page--single .letter-clauses {
    flex: 1 1 auto !important;
    display: flex !important;
    flex-direction: column !important;
    margin-top: 0.1rem;
    margin-bottom: 0.15rem;
    min-height: 0 !important;
  }

  .letter-page--single .clauses-heading {
    font-size: 9pt;
    font-weight: 700;
    margin-bottom: 0.25rem;
    flex-shrink: 0;
  }

  .letter-page--single .clauses-list {
    flex: 1 1 auto !important;
    display: flex !important;
    flex-direction: column !important;
    justify-content: space-between !important;
    min-height: 0 !important;
  }

  .letter-page--single .clauses-list li {
    font-size: 8.25pt;
    font-weight: 600;
    margin-bottom: 0 !important;
    line-height: 1.32;
    padding-left: 1.85rem;
    color: #000;

    &::before {
      width: 1.25rem;
      height: 1.25rem;
      font-size: 8pt;
      font-weight: 700;
      top: 0.05rem;
    }
  }

  .letter-page--single .acceptance-box {
    margin-top: auto !important;
    margin-bottom: 0.25rem;
    padding: 0.35rem 0.5rem;
    border: 1.5pt solid #000 !important;
    border-left: 4pt solid $kent-gold !important;
    background: #f3f4f6 !important;
    flex-shrink: 0;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .letter-page--single .acceptance-text {
    font-size: 8.25pt;
    font-weight: 600;
    line-height: 1.32;
    color: #000;
  }

  .letter-page--single .signature-section {
    margin-top: 0;
    flex-shrink: 0;
    padding-bottom: 0;
  }

  .letter-page--single .signature-intro {
    font-size: 9pt;
    font-weight: 700;
    margin-bottom: 0.25rem;
  }

  .letter-page--single .signature-row {
    gap: 1.5rem;
    margin-top: 0.15rem;
    align-items: end;
  }

  .letter-page--single .signature-space {
    height: 1.75rem;
    min-height: 1.75rem;
  }

  .letter-page--single .signature-line {
    border-top: 1.5pt solid #000 !important;
  }

  .letter-page--single .signature-name,
  .letter-page--single .signature-school {
    font-size: 9pt;
    font-weight: 700;
    margin-top: 0.15rem;
    color: #000;
  }

  .letter-page--single .signature-role {
    font-size: 8.5pt;
    font-weight: 700;
    color: #000;
  }

  .letter-page--single .signature-employee-label {
    font-size: 9pt;
    font-weight: 700;
    margin-top: 0.15rem;
    color: #000;
  }

  .letter-title__text,
  .clauses-list li::before,
  .letterhead__accent,
  .details-card,
  .detail-item,
  .acceptance-box {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
}
</style>

<style lang="scss">
@import "./kentAppointmentPrint.global.scss";
</style>
